import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

interface AboutPageProps {
  setCurrentPage?: (page: string) => void;
}

export default function AboutPage({ setCurrentPage }: AboutPageProps) {
  const handleBack = () => {
    window.location.hash = '#about';
    setCurrentPage?.('home');
  };

  return (
    <div style={{ background: 'var(--off-white)', minHeight: '100vh' }}>
      {/* Header with back button */}
      <div style={{ padding: '6rem 2rem 3rem', background: 'var(--navy)', color: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <button
            onClick={handleBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 1rem', borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white', textDecoration: 'none',
              marginBottom: '2rem', transition: 'all 0.2s',
              cursor: 'pointer',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.1)'; }}
          >
            <ArrowLeft size={16} /> Back to Home
          </button>
          <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
            About Findura Infosys
          </h1>
          <p style={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.8)', maxWidth: 600 }}>
            Discover our mission, vision, values, and the services that drive our commitment to African business excellence.
          </p>
        </div>
      </div>

      {/* Background Section */}
      <section style={{ padding: '6rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span style={{
              display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--text)', marginBottom: '1.5rem',
              padding: '0.3rem 0.9rem', borderRadius: 100,
              background: 'var(--electric-dim)', border: '1px solid var(--gray-light)',
            }}>Our Background</span>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '2rem', letterSpacing: '-0.02em', color: 'var(--text)' }}>
              Our Journey & Commitment
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'start' }}>
              <div>
                <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--text)', marginBottom: '1.5rem' }}>
                  Findura Infosys Limited, founded on March 15, 2010, in Zambia, was created to offer innovative ICT solutions that simplify complex technologies, making them more accessible and user-friendly for customers.
                </p>
                <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--text)', marginBottom: '1.5rem' }}>
                  Our goal is to provide high-quality, cost-effective ICT solutions that give businesses a competitive edge. Since its inception, Findura has become a recognized provider of software solutions across web, desktop, and mobile platforms.
                </p>
                <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--text)' }}>
                  The company adapts to new technologies to deliver cutting-edge services, offering comprehensive support that includes planning, designing, implementation, optimization, operation, and maintenance of ICT solutions.
                </p>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                style={{
                  padding: '2rem',
                  background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.08), rgba(255, 107, 0, 0.02))',
                  border: '1px solid var(--gray-light)',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
                }}
              >
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.5rem' }}>Founded</div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text)' }}>March 15, 2010</div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--gray-light)', paddingTop: '1.5rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.5rem' }}>Location</div>
                    <div style={{ fontSize: '1.1rem', color: 'var(--text)' }}>Lusaka, Zambia</div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-soft)', marginTop: '0.25rem' }}>Serving Africa & Beyond</div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--gray-light)', paddingTop: '1.5rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.5rem' }}>Core Focus</div>
                    <div style={{ fontSize: '0.95rem', color: 'var(--text)', lineHeight: 1.6 }}>Innovative ICT solutions that empower African businesses through accessible and cost-effective technology.</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section style={{ padding: '6rem 2rem', background: 'var(--navy)', color: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 style={{ fontSize: '2rem', marginBottom: '1.5rem', color: 'var(--orange)' }}>Mission</h3>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'rgba(255, 255, 255, 0.9)' }}>
                To be a leader in the development and implementation of Information Technology solutions in Zambia and in Africa.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 style={{ fontSize: '2rem', marginBottom: '1.5rem', color: 'var(--orange)' }}>Vision</h3>
              <p style={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'rgba(255, 255, 255, 0.9)' }}>
                To provide Technological solutions for the enhancement of service delivery and resource management in order to make the customer derive greater value from the use of ICT in their business and administration processes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section style={{ padding: '6rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: 'var(--text)' }}>Our Core Values</h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-soft)', marginBottom: '3rem', maxWidth: 600 }}>
              These values guide every decision we make and define our commitment to excellence.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              {[
                { title: 'Understanding', desc: 'We always strive to understand what our customer wants and how best to satisfy them.' },
                { title: 'Knowledge', desc: '"Knowledge is power and learning never stops." Our team is always up to date with IT trends and standards. Everything that happens is documented and filed to expand our knowledge base.' },
                { title: 'Integrity', desc: 'We are always ethical and above board in everything we do.' },
                { title: 'Efficiency', desc: 'We do the most in the provided time frame with optimal resources.' },
                { title: 'Excellence', desc: 'End results of our undertakings will always be as expected or even better.' },
              ].map((value, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  style={{
                    padding: '2rem',
                    border: '1px solid var(--gray-light)',
                    borderRadius: 16,
                    background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.04), transparent)',
                  }}
                >
                  <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', marginBottom: '0.75rem' }}>{value.title}</h4>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.7, color: 'var(--text-soft)' }}>{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Business Objectives Section */}
      <section style={{ padding: '6rem 2rem', background: 'var(--electric-dim)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '3rem', color: 'var(--text)' }}>Our Business Objectives</h2>

            <div style={{ display: 'grid', gap: '2rem' }}>
              {[
                'Enable Findura\'s customers to derive tangible business value from their investment in ICT.',
                'Timely delivery of solutions at promised quality levels and within planned resources.',
                'Skill transfer to the customer in the course of all our undertakings. Transparency across the board in all our dealings.',
              ].map((objective, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  style={{
                    display: 'flex', gap: '2rem', alignItems: 'start',
                    padding: '2rem',
                    background: 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid var(--gray-light)',
                    borderRadius: 12,
                    borderLeft: '4px solid var(--orange)',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 50, flexShrink: 0,
                    background: 'var(--orange)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                  }}>
                    {i + 1}
                  </div>
                  <p style={{ fontSize: '1.05rem', lineHeight: 1.7, color: 'var(--text)' }}>{objective}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ padding: '6rem 2rem', background: 'var(--navy)', color: 'white', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>Ready to Partner with Us?</h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
            Let's discuss how Findura Infosys can empower your business with innovative technology solutions.
          </p>
          <a
            href="/#contact"
            style={{
              display: 'inline-block',
              padding: '1rem 2.5rem', borderRadius: 12,
              background: 'var(--orange)', color: 'white',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '1rem',
              textDecoration: 'none',
              transition: 'all 0.3s',
              boxShadow: '0 0 30px rgba(255, 107, 0, 0.3)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 50px rgba(255, 107, 0, 0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255, 107, 0, 0.3)'; }}
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
}
